import React from "react";
import { Link } from "react-router-dom";
import { Building2, CalendarDays, CreditCard, FileText, Home, Wallet, Wrench } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import {
  EmptyState,
  formatMoney,
  InfoCard,
  SectionCard,
  SummaryTile,
  useTenantPortalData,
} from "./tenantPortalShared.jsx";
import { useToast } from "../contexts/ToastContext";

const quickLinks = [
  { label: "Open invoices", to: "/tenant/invoices", icon: FileText },
  { label: "View payments", to: "/tenant/payments", icon: Wallet },
  { label: "Open documents", to: "/tenant/documents", icon: FileText },
  { label: "Manage requests", to: "/tenant/requests", icon: Wrench },
];

export default function TenantHomePage({ isDark, setIsDark, currency, setCurrency }) {
  const { user } = useAuth();
  const toast = useToast();
  const {
    tenant,
    invoices,
    payments,
    maintenanceTickets,
    loading,
    leaseEndText: currentLeaseEndText,
    paymentSummary,
    invoiceSummary,
  } = useTenantPortalData(user, toast);

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Resident Portal"
      title="Welcome home"
      description="Review your tenancy, balances, and service activity from your resident account."
    >
      <section
        className={`rounded-[2rem] border p-6 shadow-sm ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <p className={`text-sm leading-7 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          {loading
            ? "Loading your resident account details..."
            : tenant
            ? "Your resident home shows only the account details relevant to your tenancy."
            : "Your tenant login is active, but no linked tenant record could be loaded yet. Ask the SpaceM8 admin to confirm your tenant email and tenant_id linkage."}
        </p>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <InfoCard icon={Building2} label="Property" value={tenant?.property_name} isDark={isDark} />
        <InfoCard icon={Home} label="Unit" value={tenant?.unit} isDark={isDark} />
        <InfoCard
          icon={CreditCard}
          label={`Monthly rent (${currency})`}
          value={tenant?.monthly_rent != null ? Number(tenant.monthly_rent).toLocaleString() : null}
          isDark={isDark}
        />
        <InfoCard icon={CalendarDays} label="Lease end" value={currentLeaseEndText} isDark={isDark} />
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <SectionCard
          title="Account snapshot"
          description="A quick summary of invoices, payments, and requests linked to your tenancy."
          isDark={isDark}
        >
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <SummaryTile label="Invoices" value={invoiceSummary.count} isDark={isDark} />
            <SummaryTile label="Outstanding" value={formatMoney(invoiceSummary.outstanding, currency)} isDark={isDark} />
            <SummaryTile label="Payments" value={paymentSummary.total} isDark={isDark} />
            <SummaryTile label="Requests" value={maintenanceTickets.length} isDark={isDark} />
          </div>
        </SectionCard>

        <SectionCard
          title="Quick access"
          description="Open the main parts of your resident portal."
          isDark={isDark}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {quickLinks.map((item) => {
              const Icon = item.icon;

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`rounded-2xl border p-4 transition ${
                    isDark
                      ? "border-slate-800 bg-slate-950 text-white hover:bg-slate-800"
                      : "border-slate-200 bg-slate-50 text-slate-900 hover:bg-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="rounded-2xl bg-sky-100 p-3 text-sky-700">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{item.label}</p>
                      <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>Resident section</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </SectionCard>
      </section>

      <section className="grid gap-6 xl:grid-cols-2">
        <SectionCard
          title="Recent invoice status"
          description="Your latest resident billing activity."
          isDark={isDark}
        >
          {invoices.length === 0 ? (
            <EmptyState
              title="No invoices yet"
              body="When invoices are issued for this tenancy, they will appear here."
              isDark={isDark}
            />
          ) : (
            <div className="space-y-3">
              {invoices.slice(0, 3).map((invoice) => (
                <div
                  key={invoice.id}
                  className={`rounded-2xl border p-4 ${
                    isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{invoice.id}</p>
                      <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        Due {invoice.due_date || "Not available"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                        {formatMoney(invoice.amount, currency)}
                      </p>
                      <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>{invoice.status}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="Recent payment activity"
          description="A quick view of recent rent payment records."
          isDark={isDark}
        >
          {payments.length === 0 ? (
            <EmptyState
              title="No payment history yet"
              body="When rent payments are linked to this tenancy, they will appear here."
              isDark={isDark}
            />
          ) : (
            <div className="space-y-3">
              {payments.slice(0, 3).map((payment) => (
                <div
                  key={payment.id}
                  className={`rounded-2xl border p-4 ${
                    isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                        {formatMoney(payment.amount, currency)}
                      </p>
                      <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        Due {payment.due_date || "Not available"}
                      </p>
                    </div>
                    <p className={`text-xs font-semibold ${isDark ? "text-slate-300" : "text-slate-600"}`}>{payment.status}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </section>
    </AppLayout>
  );
}
